<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        <span class="text-h4">Module Management</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          Create Module
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Modules List -->
        <v-data-table
          :headers="headers"
          :items="modules"
          :loading="loading"
          class="elevation-1"
        >
          <template v-slot:item.actions="{ item }">
            <v-btn small text color="primary" @click="viewModule(item)">
              <v-icon small>mdi-eye</v-icon>
              View
            </v-btn>
            <v-btn small text color="purple" @click="showMCPEndpoint(item)">
              <v-icon small>mdi-api</v-icon>
              MCP
            </v-btn>
            <v-btn small text color="error" @click="deleteModule(item)">
              <v-icon small>mdi-delete</v-icon>
              Delete
            </v-btn>
          </template>

          <template v-slot:item.users="{ item }">
            {{ item.users ? item.users.length : 0 }}
          </template>

          <template v-slot:item.workflow_templates="{ item }">
            {{ item.workflow_templates ? item.workflow_templates.length : 0 }}
          </template>

          <template v-slot:item.entity_types="{ item }">
            {{ item.entity_types ? item.entity_types.length : 0 }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Create Module Dialog -->
    <v-dialog v-model="createDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Create New Module</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="createForm">
            <v-text-field
              v-model="newModule.name"
              label="Module Name"
              required
              :rules="[(v) => !!v || 'Name is required']"
            ></v-text-field>
            <v-textarea
              v-model="newModule.description"
              label="Description"
              rows="3"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="createDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="createModule" :loading="creating">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- View/Edit Module Dialog -->
    <v-dialog v-model="viewDialog" max-width="900px" scrollable>
      <v-card v-if="selectedModule">
        <v-card-title>
          <span class="text-h5">{{ selectedModule.name }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="viewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="max-height: 70vh">
          <v-form ref="editForm">
            <v-text-field
              v-model="selectedModule.name"
              label="Module Name"
              required
            ></v-text-field>
            <v-textarea
              v-model="selectedModule.description"
              label="Description"
              rows="3"
            ></v-textarea>

            <v-btn
              small
              color="primary"
              @click="updateModule"
              :loading="updating"
              class="mb-4"
            >
              Update Basic Info
            </v-btn>
          </v-form>

          <v-divider class="my-4"></v-divider>

          <!-- Users Section -->
          <v-expansion-panels class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span class="font-weight-bold">
                  Users ({{ selectedModule.users ? selectedModule.users.length : 0 }})
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mb-2">
                  <v-col cols="9">
                    <v-text-field
                      v-model="newUserId"
                      label="User ID"
                      type="number"
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-btn small color="primary" @click="addUser" block>
                      Add User
                    </v-btn>
                  </v-col>
                </v-row>
                <v-list dense>
                  <v-list-item
                    v-for="user in getUsersDisplay()"
                    :key="user.id"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ user.username || `User ID: ${user.id}` }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="user.email">
                        {{ user.email }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        x-small
                        icon
                        color="error"
                        @click="removeUser(user.id)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Workflow Templates Section -->
          <v-expansion-panels class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span class="font-weight-bold">
                  Workflow Templates ({{
                    selectedModule.workflow_templates
                      ? selectedModule.workflow_templates.length
                      : 0
                  }})
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mb-2">
                  <v-col cols="6">
                    <v-text-field
                      v-model="newWorkflowTemplateId"
                      label="Workflow Template ID"
                      type="number"
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      v-model="newWorkflowVersion"
                      label="Version (optional)"
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-btn small color="primary" @click="addWorkflowTemplate" block>
                      Add
                    </v-btn>
                  </v-col>
                </v-row>
                <v-list dense>
                  <v-list-item
                    v-for="wft in getWorkflowTemplatesDisplay()"
                    :key="wft.id"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ wft.name || `Workflow ID: ${wft.id}` }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="wft.version">
                        Version: {{ wft.version }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action class="flex-row">
                      <v-text-field
                        v-model="workflowVersionEdit[wft.id]"
                        label="Version"
                        dense
                        hide-details
                        style="width: 100px"
                        class="mr-2"
                      ></v-text-field>
                      <v-btn
                        x-small
                        color="primary"
                        @click="updateWorkflowVersion(wft.id)"
                        class="mr-2"
                      >
                        Update
                      </v-btn>
                      <v-btn
                        x-small
                        icon
                        color="error"
                        @click="removeWorkflowTemplate(wft.id)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Entity Types Section -->
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header>
                <span class="font-weight-bold">
                  Entity Types ({{
                    selectedModule.entity_types ? selectedModule.entity_types.length : 0
                  }})
                </span>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row class="mb-2">
                  <v-col cols="9">
                    <v-text-field
                      v-model="newEntityTypeUuid"
                      label="Entity Type UUID"
                      dense
                      hide-details
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3">
                    <v-btn small color="primary" @click="addEntityType" block>
                      Add
                    </v-btn>
                  </v-col>
                </v-row>
                <v-list dense>
                  <v-list-item
                    v-for="et in getEntityTypesDisplay()"
                    :key="et.uuid"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ et.name || et.uuid }}
                      </v-list-item-title>
                      <v-list-item-subtitle v-if="et.name && et.kind">
                        {{ et.kind }} - {{ et.uuid }}
                      </v-list-item-subtitle>
                      <v-list-item-subtitle v-else-if="et.name">
                        {{ et.uuid }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn
                        x-small
                        icon
                        color="error"
                        @click="removeEntityType(et.uuid)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- MCP Server Endpoint Dialog -->
    <v-dialog v-model="mcpDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <v-icon left color="purple">mdi-api</v-icon>
          <span class="text-h5">MCP Server Endpoint</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="mcpDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text v-if="selectedMCPModule">
          <v-alert type="info" text class="mb-4">
            Use this endpoint to connect Claude Code or other MCP clients to your workflows.
          </v-alert>

          <!-- Company ID Selection -->
          <v-card outlined class="mb-4">
            <v-card-subtitle class="pb-0">
              <strong>Company Context</strong>
            </v-card-subtitle>
            <v-card-text>
              <p class="text-caption mb-2">
                Workflows will execute in this company context:
              </p>
              <v-text-field
                v-model="mcpCompanyId"
                label="Company ID"
                type="number"
                outlined
                dense
                hint="The company ID where workflows will be executed"
                persistent-hint
                :rules="[v => !!v || 'Company ID is required']"
              ></v-text-field>
            </v-card-text>
          </v-card>

          <!-- MCP Endpoint URL -->
          <v-card outlined class="mb-4">
            <v-card-subtitle class="pb-0">
              <strong>MCP Server Endpoint</strong>
            </v-card-subtitle>
            <v-card-text>
              <v-text-field
                :value="getMCPEndpoint()"
                readonly
                outlined
                dense
                hide-details
                append-icon="mdi-content-copy"
                @click:append="copyToClipboard(getMCPEndpoint(), 'Endpoint URL')"
              ></v-text-field>
            </v-card-text>
          </v-card>

          <!-- Claude Code Configuration -->
          <v-card outlined class="mb-4">
            <v-card-subtitle class="pb-0">
              <strong>Claude Code Configuration</strong>
            </v-card-subtitle>
            <v-card-text>
              <p class="text-caption mb-2">
                Add this to your Claude Code MCP settings:
              </p>
              <v-textarea
                :value="getClaudeCodeConfig()"
                readonly
                outlined
                dense
                rows="12"
                auto-grow
                hide-details
                class="mb-2"
                style="font-family: monospace; font-size: 12px;"
              ></v-textarea>
              <v-btn
                small
                color="primary"
                @click="copyToClipboard(getClaudeCodeConfig(), 'Configuration')"
              >
                <v-icon small left>mdi-content-copy</v-icon>
                Copy Configuration
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Instructions -->
          <v-card outlined>
            <v-card-subtitle class="pb-0">
              <strong>Setup Instructions</strong>
            </v-card-subtitle>
            <v-card-text>
              <ol class="text-body-2">
                <li>Enter your Company ID above (required for workflow execution context)</li>
                <li>Copy the configuration</li>
                <li>Open Claude Code MCP settings</li>
                <li>Add the configuration to your <code>mcpServers</code> section</li>
                <li>Replace <code>YOUR_TOKEN</code> with your authentication token</li>
                <li>Restart Claude Code</li>
                <li>Your workflows will be available as tools to Claude!</li>
              </ol>
              <v-alert type="warning" dense text class="mt-3">
                <strong>Important:</strong> All workflows will execute in the context of Company ID {{ mcpCompanyId || '[not set]' }}.
                Make sure you have the correct company ID.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="mcpDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { query_dispatcher, sync_command_dispatcher } from "@/api/client";

export default {
  name: "ModuleManager",
  data() {
    return {
      modules: [],
      loading: false,
      createDialog: false,
      viewDialog: false,
      mcpDialog: false,
      creating: false,
      updating: false,
      selectedModule: null,
      selectedMCPModule: null,
      mcpCompanyId: 1,  // Default company ID
      newModule: {
        name: "",
        description: "",
      },
      // For adding items
      newUserId: null,
      newWorkflowTemplateId: null,
      newWorkflowVersion: "",
      newEntityTypeUuid: "",
      workflowVersionEdit: {},
      // Snackbar
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      // Table headers
      headers: [
        { text: "ID", value: "id" },
        { text: "Name", value: "name" },
        { text: "Description", value: "description" },
        { text: "Users", value: "users" },
        { text: "Workflows", value: "workflow_templates" },
        { text: "Entity Types", value: "entity_types" },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  mounted() {
    this.loadModules();
  },
  methods: {
    async loadModules() {
      this.loading = true;
      try {
        const response = await query_dispatcher.dispatch("modules.get_all", {});
        if (response.status === "200") {
          this.modules = response.data;
        }
      } catch (error) {
        this.showSnackbar("Error loading modules: " + error.message, "error");
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.newModule = { name: "", description: "" };
      this.createDialog = true;
    },
    async createModule() {
      if (!this.$refs.createForm.validate()) return;

      this.creating = true;
      try {
        const response = await sync_command_dispatcher.dispatch("modules.create", {
          name: this.newModule.name,
          description: this.newModule.description,
        });

        if (response.output && response.output.status === "201") {
          this.showSnackbar("Module created successfully", "success");
          this.createDialog = false;
          this.loadModules();
        } else {
          this.showSnackbar("Error creating module", "error");
        }
      } catch (error) {
        this.showSnackbar("Error creating module: " + error.message, "error");
      } finally {
        this.creating = false;
      }
    },
    async viewModule(module) {
      this.selectedModule = { ...module };
      this.workflowVersionEdit = {};
      // Initialize version edit fields
      if (this.selectedModule.workflow_templates_versions) {
        this.selectedModule.workflow_templates.forEach((wftId) => {
          this.workflowVersionEdit[wftId] =
            this.selectedModule.workflow_templates_versions[wftId] || "";
        });
      }
      this.viewDialog = true;
    },
    async updateModule() {
      this.updating = true;
      try {
        const response = await sync_command_dispatcher.dispatch("modules.update", {
          module_id: this.selectedModule.id,
          name: this.selectedModule.name,
          description: this.selectedModule.description,
        });

        if (response.output && response.output.status === "200") {
          this.showSnackbar("Module updated successfully", "success");
          this.loadModules();
        } else {
          this.showSnackbar("Error updating module", "error");
        }
      } catch (error) {
        this.showSnackbar("Error updating module: " + error.message, "error");
      } finally {
        this.updating = false;
      }
    },
    async deleteModule(module) {
      if (!confirm(`Are you sure you want to delete module "${module.name}"?`)) {
        return;
      }

      try {
        const response = await sync_command_dispatcher.dispatch("modules.delete", {
          module_id: module.id,
        });

        if (response.output && response.output.status === "200") {
          this.showSnackbar("Module deleted successfully", "success");
          this.loadModules();
        } else {
          this.showSnackbar("Error deleting module", "error");
        }
      } catch (error) {
        this.showSnackbar("Error deleting module: " + error.message, "error");
      }
    },
    async addUser() {
      if (!this.newUserId) return;

      try {
        const response = await sync_command_dispatcher.dispatch(
          "modules.add_user",
          {
            module_id: this.selectedModule.id,
            target_user_id: parseInt(this.newUserId),
          }
        );

        if (response.output && response.output.status === "200") {
          this.showSnackbar("User added successfully", "success");
          if (!this.selectedModule.users) this.selectedModule.users = [];
          this.selectedModule.users.push(parseInt(this.newUserId));
          this.newUserId = null;
          this.loadModules();
        } else {
          this.showSnackbar(response.output.data || "Error adding user", "error");
        }
      } catch (error) {
        this.showSnackbar("Error adding user: " + error.message, "error");
      }
    },
    async removeUser(userId) {
      try {
        const response = await sync_command_dispatcher.dispatch(
          "modules.remove_user",
          {
            module_id: this.selectedModule.id,
            target_user_id: userId,
          }
        );

        if (response.output && response.output.status === "200") {
          this.showSnackbar("User removed successfully", "success");
          this.selectedModule.users = this.selectedModule.users.filter(
            (id) => id !== userId
          );
          this.loadModules();
        } else {
          this.showSnackbar("Error removing user", "error");
        }
      } catch (error) {
        this.showSnackbar("Error removing user: " + error.message, "error");
      }
    },
    async addWorkflowTemplate() {
      if (!this.newWorkflowTemplateId) return;

      try {
        const response = await sync_command_dispatcher.dispatch(
          "modules.add_workflow_template",
          {
            module_id: this.selectedModule.id,
            workflow_template_id: parseInt(this.newWorkflowTemplateId),
            version: this.newWorkflowVersion || null,
          }
        );

        if (response.output && response.output.status === "200") {
          this.showSnackbar("Workflow template added successfully", "success");
          if (!this.selectedModule.workflow_templates)
            this.selectedModule.workflow_templates = [];
          this.selectedModule.workflow_templates.push(
            parseInt(this.newWorkflowTemplateId)
          );
          if (this.newWorkflowVersion) {
            if (!this.selectedModule.workflow_templates_versions)
              this.selectedModule.workflow_templates_versions = {};
            this.selectedModule.workflow_templates_versions[
              parseInt(this.newWorkflowTemplateId)
            ] = this.newWorkflowVersion;
          }
          this.newWorkflowTemplateId = null;
          this.newWorkflowVersion = "";
          this.loadModules();
        } else {
          this.showSnackbar(
            response.output.data || "Error adding workflow template",
            "error"
          );
        }
      } catch (error) {
        this.showSnackbar(
          "Error adding workflow template: " + error.message,
          "error"
        );
      }
    },
    async removeWorkflowTemplate(wftId) {
      try {
        const response = await sync_command_dispatcher.dispatch(
          "modules.remove_workflow_template",
          {
            module_id: this.selectedModule.id,
            workflow_template_id: wftId,
          }
        );

        if (response.output && response.output.status === "200") {
          this.showSnackbar("Workflow template removed successfully", "success");
          this.selectedModule.workflow_templates =
            this.selectedModule.workflow_templates.filter((id) => id !== wftId);
          this.loadModules();
        } else {
          this.showSnackbar("Error removing workflow template", "error");
        }
      } catch (error) {
        this.showSnackbar(
          "Error removing workflow template: " + error.message,
          "error"
        );
      }
    },
    async updateWorkflowVersion(wftId) {
      const version = this.workflowVersionEdit[wftId];
      if (!version) return;

      try {
        const response = await sync_command_dispatcher.dispatch(
          "modules.update_workflow_version",
          {
            module_id: this.selectedModule.id,
            workflow_template_id: wftId,
            version: version,
          }
        );

        if (response.output && response.output.status === "200") {
          this.showSnackbar("Version updated successfully", "success");
          if (!this.selectedModule.workflow_templates_versions)
            this.selectedModule.workflow_templates_versions = {};
          this.selectedModule.workflow_templates_versions[wftId] = version;
          this.loadModules();
        } else {
          this.showSnackbar("Error updating version", "error");
        }
      } catch (error) {
        this.showSnackbar("Error updating version: " + error.message, "error");
      }
    },
    async addEntityType() {
      if (!this.newEntityTypeUuid) return;

      try {
        const response = await sync_command_dispatcher.dispatch(
          "modules.add_entity_type",
          {
            module_id: this.selectedModule.id,
            entity_type_uuid: this.newEntityTypeUuid,
          }
        );

        if (response.output && response.output.status === "200") {
          this.showSnackbar("Entity type added successfully", "success");
          if (!this.selectedModule.entity_types)
            this.selectedModule.entity_types = [];
          this.selectedModule.entity_types.push(this.newEntityTypeUuid);
          this.newEntityTypeUuid = "";
          this.loadModules();
        } else {
          this.showSnackbar(
            response.output.data || "Error adding entity type",
            "error"
          );
        }
      } catch (error) {
        this.showSnackbar("Error adding entity type: " + error.message, "error");
      }
    },
    async removeEntityType(uuid) {
      try {
        const response = await sync_command_dispatcher.dispatch(
          "modules.remove_entity_type",
          {
            module_id: this.selectedModule.id,
            entity_type_uuid: uuid,
          }
        );

        if (response.output && response.output.status === "200") {
          this.showSnackbar("Entity type removed successfully", "success");
          this.selectedModule.entity_types =
            this.selectedModule.entity_types.filter((id) => id !== uuid);
          this.loadModules();
        } else {
          this.showSnackbar("Error removing entity type", "error");
        }
      } catch (error) {
        this.showSnackbar("Error removing entity type: " + error.message, "error");
      }
    },
    showSnackbar(message, color = "success") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    getUsersDisplay() {
      if (!this.selectedModule) return [];
      if (this.selectedModule.users_detail && this.selectedModule.users_detail.length > 0) {
        return this.selectedModule.users_detail;
      }
      // Fallback to just IDs
      return (this.selectedModule.users || []).map(id => ({ id }));
    },
    getWorkflowTemplatesDisplay() {
      if (!this.selectedModule) return [];
      if (this.selectedModule.workflow_templates_detail && this.selectedModule.workflow_templates_detail.length > 0) {
        return this.selectedModule.workflow_templates_detail;
      }
      // Fallback to just IDs
      return (this.selectedModule.workflow_templates || []).map(id => ({
        id,
        version: this.selectedModule.workflow_templates_versions ? this.selectedModule.workflow_templates_versions[id] : null
      }));
    },
    getEntityTypesDisplay() {
      if (!this.selectedModule) return [];
      if (this.selectedModule.entity_types_detail && this.selectedModule.entity_types_detail.length > 0) {
        return this.selectedModule.entity_types_detail;
      }
      // Fallback to just UUIDs
      return (this.selectedModule.entity_types || []).map(uuid => ({ uuid }));
    },
    // MCP Server methods
    showMCPEndpoint(module) {
      this.selectedMCPModule = module;
      this.mcpDialog = true;
    },
    getMCPEndpoint() {
      if (!this.selectedMCPModule || !this.mcpCompanyId) return '';
      const hostname = window.location.hostname;
      let baseUrl;
      if (hostname.includes('stage')) {
        baseUrl = 'https://stage.inmatic.ai';
      } else if (hostname.includes('app')) {
        baseUrl = 'https://api.inmatic.ai';
      } else {
        baseUrl = window.location.origin;
      }
      return `${baseUrl}/api/mcp/modules/${this.selectedMCPModule.id}/?company_id=${this.mcpCompanyId}`;
    },
    getClaudeCodeConfig() {
      if (!this.selectedMCPModule) return '';
      const moduleName = this.selectedMCPModule.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');

      const config = {
        mcpServers: {
          [moduleName]: {
            url: this.getMCPEndpoint(),
            type: "http",
            headers: {
              Authorization: "Token YOUR_TOKEN"
            }
          }
        }
      };

      return JSON.stringify(config, null, 2);
    },
    copyToClipboard(text, label) {
      navigator.clipboard.writeText(text).then(() => {
        this.showSnackbar(`${label} copied to clipboard!`, 'success');
      }).catch(err => {
        this.showSnackbar(`Failed to copy: ${err.message}`, 'error');
      });
    },
  },
};
</script>

<style scoped>
.v-list-item-action {
  display: flex;
  align-items: center;
}
</style>
